import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/site/Seo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (tries.current >= 8) { setState("pending"); return; }
        tries.current += 1;
        setTimeout(poll, 2000);
      } catch {
        if (!active) return;
        if (tries.current >= 8) { setState("error"); return; }
        tries.current += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center bg-linen px-6" data-testid="payment-success">
      <Seo title="Membership Confirmed | Psalm 30:2 Chiropractic" />
      <div className="max-w-md text-center">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-brass animate-spin mb-6" />
            <h1 className="font-serif text-3xl text-espresso">Confirming your membership…</h1>
            <p className="text-taupe mt-3">One moment while we verify your payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-sage mb-6" strokeWidth={1.4} />
            <h1 className="font-serif text-4xl text-espresso" data-testid="payment-success-title">You're all set!</h1>
            <p className="text-espressosoft/75 mt-4">Your membership is active. Welcome to ongoing wellness care with Psalm 30:2.</p>
            <Link to="/portal" className="mt-8 inline-block bg-espresso text-ivory rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-brass transition-colors" data-testid="payment-success-portal">
              Go to My Rewards
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={48} className="mx-auto text-brass mb-6" />
            <h1 className="font-serif text-3xl text-espresso">Almost there</h1>
            <p className="text-taupe mt-3">Your payment is processing. It may take a moment to confirm — check your portal shortly.</p>
            <Link to="/portal" className="mt-8 inline-block border border-espresso/30 rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-ivory transition-colors">Go to My Rewards</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-destructive mb-6" />
            <h1 className="font-serif text-3xl text-espresso">We couldn't confirm this</h1>
            <p className="text-taupe mt-3">If you were charged, please contact us and we'll sort it out right away.</p>
            <Link to="/memberships" className="mt-8 inline-block border border-espresso/30 rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-ivory transition-colors">Back to Memberships</Link>
          </>
        )}
      </div>
    </div>
  );
}
